import { formatJobNumber } from '@fieldops/shared'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { humanise, jobStatusLabel } from '../../../components/ui.js'
import { jobClientName, jobPlaceName, jobSuburb } from './labels.js'

/**
 * Finding a job on the board you are already looking at.
 *
 * There is no board-search endpoint, and inventing one would mean a query that
 * ranges outside the day on screen and then cannot show you what it found. So
 * this searches the window already loaded and says so in the placeholder. A
 * dispatcher asking "where did Kensington end up today" gets an answer; one
 * asking "when did we last go to Kensington" is asking the Jobs list.
 *
 * Non-matching bookings fade rather than disappearing. A board with the gaps
 * removed is a different board, and the point of the search is to locate
 * something in the day's real shape, not to filter the day down to it.
 *
 * The term travels by context rather than as a prop, because otherwise every
 * board, lane and column between the toolbar and a block has to carry a
 * predicate it does not use.
 */

const BoardSearchContext = createContext<string>('')

export function BoardSearchProvider({ term, children }: { term: string; children: ReactNode }) {
  const normalised = useMemo(() => term.trim().toLowerCase(), [term])

  return <BoardSearchContext.Provider value={normalised}>{children}</BoardSearchContext.Provider>
}

export function useBoardSearch(): string {
  return useContext(BoardSearchContext)
}

type Searchable = {
  number: number
  displayNumber?: string | null
  title?: string | null
  status?: string | null
  priority?: string | null
  client: { name: string }
  category?: { name: string } | null
  site?: {
    name?: string | null
    address?: {
      raw?: string | null
      street?: string | null
      suburb: string | null
      city?: string | null
    } | null
  } | null
}

/**
 * Number, client, street, suburb, title, category and the status on the card.
 *
 * Words are AND — "jacaranda salisbury" is the house on that street, not every
 * Jacaranda or every Salisbury. The number matches bare or with its hash,
 * because people type both and a dispatcher reading one off a card sees `#4187`.
 */
export function jobMatches(job: Searchable, term: string): boolean {
  const tokens = term.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return true

  const address = job.site?.address
  const haystack = [
    jobPlaceName(job),
    jobClientName(job),
    jobSuburb(job),
    job.title,
    job.category?.name,
    job.site?.name,
    address?.raw,
    address?.street,
    address?.suburb,
    address?.city,
    job.displayNumber,
    formatJobNumber(job),
    String(job.number),
    `#${job.number}`,
    job.status,
    job.status ? jobStatusLabel(job.status) : null,
    job.priority,
    job.priority ? humanise(job.priority) : null,
  ]
    .map((value) => value?.toLowerCase())
    .filter((value): value is string => Boolean(value))

  return tokens.every((token) => haystack.some((value) => value.includes(token)))
}
